/**
 * Personal Access Tokens management page.
 *
 * Lets engineers create, list, and revoke PATs that authenticate against
 * Sherlock's MCP server. Tokens are sha256-hashed server-side; the plain
 * value is returned exactly once at creation and surfaced here in a
 * one-shot reveal modal.
 *
 * Aesthetic: retro JetBrains Mono, amber accent, 8dp rhythm. Touch
 * targets ≥44px. Destructive emphasis (error.main) on revoke. Copy
 * confirmation is announced via aria-live; no toast.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { CopyableField, SectionHeader } from '@/components/CopyableField';
import { ApiError, pats as patsApi } from '@/helpers/ApiHelper';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import type { PatCreateResponse, PatRow } from '@/types/pat';

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: PatRow[] }
  | { kind: 'error'; message: string };

const COPY_RESET_MS_DEFAULT = 1500;
const COPY_RESET_MS_REDUCED = 600;

export function PatsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const reducedMotion = useReducedMotion();

  const [list, setList] = useState<ListState>({ kind: 'loading' });
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<PatCreateResponse | null>(null);
  const [revealCopied, setRevealCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<PatRow | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const announcementTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchList = useCallback(async () => {
    setList({ kind: 'loading' });
    try {
      const page = await patsApi.list();
      setList({ kind: 'ready', rows: page.results });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.detail : 'Failed to load personal access tokens';
      setList({ kind: 'error', message });
    }
  }, []);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // ?new=1 deep-link from ShareToClaudeDialog opens the create form on mount.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') setCreating(true);
  }, [location.search]);

  // Clean up the copy-state timer on unmount.
  useEffect(() => {
    return () => {
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
      if (announcementTimer.current) clearTimeout(announcementTimer.current);
    };
  }, []);

  const announce = useCallback((message: string) => {
    setAnnouncement(message);
    if (announcementTimer.current) clearTimeout(announcementTimer.current);
    announcementTimer.current = setTimeout(() => setAnnouncement(''), 2000);
  }, []);

  const handleSubmitCreate = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const name = createName.trim();
      if (!name) return;
      setSubmitting(true);
      setSubmitError(null);
      try {
        const created = await patsApi.create({ name });
        setRevealed(created);
        setRevealCopied(false);
        setCreating(false);
        setCreateName('');
      } catch (err) {
        setSubmitError(err instanceof ApiError ? err.detail : 'Failed to create token');
      } finally {
        setSubmitting(false);
      }
    },
    [createName],
  );

  const handleCopyReveal = useCallback(() => {
    if (!revealed) return;
    void navigator.clipboard.writeText(revealed.token);
    setRevealCopied(true);
    announce('Personal access token copied');
    if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    copyResetTimer.current = setTimeout(
      () => setRevealCopied(false),
      reducedMotion ? COPY_RESET_MS_REDUCED : COPY_RESET_MS_DEFAULT,
    );
  }, [revealed, announce, reducedMotion]);

  const handleConfirmRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    setRevokeBusy(true);
    try {
      await patsApi.revoke(revokeTarget.id);
      announce(`Token "${revokeTarget.name}" revoked`);
      setRevokeTarget(null);
      await fetchList();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.detail : 'Failed to revoke token');
    } finally {
      setRevokeBusy(false);
    }
  }, [revokeTarget, fetchList, announce]);

  const handleCloseReveal = useCallback(() => {
    setRevealed(null);
    setRevealCopied(false);
    void fetchList();
  }, [fetchList]);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 3, py: 5 }}>
      {/* Polite aria-live region for copy + revoke confirmations */}
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: -10000,
          width: 1,
          height: 1,
          overflow: 'hidden',
        }}
      >
        {announcement}
      </Box>

      <Stack spacing={4}>
        <SectionHeader
          title="personal access tokens"
          subtitle="for claude code / mcp"
          primary
        />

        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640 }}>
          Personal access tokens let Claude Code (and other MCP clients) talk to
          Sherlock on your behalf. Tokens are scoped to your account and can be
          revoked at any time. Anyone holding a token can list your investigations,
          mint share links, and read unresolved issues — treat them like passwords.
        </Typography>

        {!creating && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreating(true)}
            sx={{
              alignSelf: 'flex-start',
              minHeight: 44,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 13,
              textTransform: 'none',
              letterSpacing: '0.02em',
            }}
          >
            + new personal access token
          </Button>
        )}

        {creating && (
          <Box
            component="form"
            onSubmit={handleSubmitCreate}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 3,
            }}
          >
            <Stack spacing={2}>
              <SectionHeader title="new token" />
              <TextField
                autoFocus
                fullWidth
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="claude-cli@laptop"
                inputProps={{
                  maxLength: 120,
                  'aria-label': 'token name',
                }}
                helperText="Give it a memorable label — e.g. claude-cli@laptop"
                disabled={submitting}
              />
              {submitError && <Alert severity="error">{submitError}</Alert>}
              <Stack direction="row" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting || createName.trim().length === 0}
                  startIcon={
                    submitting ? <CircularProgress size={14} color="inherit" /> : undefined
                  }
                  sx={{
                    minHeight: 44,
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 13,
                    textTransform: 'none',
                  }}
                >
                  {submitting ? 'creating…' : 'create'}
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setCreating(false);
                    setCreateName('');
                    setSubmitError(null);
                    if (new URLSearchParams(location.search).get('new') === '1') {
                      navigate('/settings/pats', { replace: true });
                    }
                  }}
                  disabled={submitting}
                  sx={{
                    minHeight: 44,
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 13,
                    textTransform: 'none',
                    color: 'text.secondary',
                  }}
                >
                  cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Token list */}
        <Box>
          {list.kind === 'loading' && (
            <Stack spacing={1}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rectangular" height={56} />
              ))}
            </Stack>
          )}

          {list.kind === 'error' && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => void fetchList()}>
                  retry
                </Button>
              }
            >
              {list.message}
            </Alert>
          )}

          {list.kind === 'ready' && list.rows.length === 0 && !creating && (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 4,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  color: 'text.secondary',
                  fontSize: 14,
                }}
              >
                no tokens yet
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                create one above to connect Claude Code to Sherlock.
              </Typography>
            </Box>
          )}

          {list.kind === 'ready' && list.rows.length > 0 && (
            <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
              {list.rows.map((row) => (
                <PatRowItem
                  key={row.id}
                  row={row}
                  onRevoke={() => setRevokeTarget(row)}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>

      {/* One-shot reveal modal */}
      <Dialog
        open={revealed !== null}
        onClose={handleCloseReveal}
        maxWidth="sm"
        fullWidth
        transitionDuration={reducedMotion ? 0 : undefined}
      >
        <DialogTitle
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 14,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          ▸ new token created
          <IconButton
            aria-label="close"
            onClick={handleCloseReveal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {revealed && (
            <Stack spacing={3} sx={{ pt: 1 }}>
              <Alert severity="warning" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
                This is the only time you'll see this token. Copy it now — Sherlock
                stores only a hash and cannot show it again.
              </Alert>
              <CopyableField
                value={revealed.token}
                ariaLabel="Personal access token"
                onCopy={handleCopyReveal}
                copied={revealCopied}
                monospace
              />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Token name: <strong>{revealed.name}</strong> ·{' '}
                prefix <code>{revealed.token_prefix}</code>
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleCloseReveal}
            sx={{
              minHeight: 44,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 13,
              textTransform: 'none',
            }}
          >
            i've copied it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirm dialog */}
      <Dialog
        open={revokeTarget !== null}
        onClose={() => !revokeBusy && setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
        transitionDuration={reducedMotion ? 0 : undefined}
      >
        <DialogTitle
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 14,
            textTransform: 'uppercase',
          }}
        >
          revoke token?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Revoke <strong>{revokeTarget?.name}</strong>? This cannot be undone.
            Any tool using this token will lose access immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="text"
            onClick={() => setRevokeTarget(null)}
            disabled={revokeBusy}
            sx={{
              minHeight: 44,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 13,
              textTransform: 'none',
              color: 'text.secondary',
            }}
          >
            cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleConfirmRevoke()}
            disabled={revokeBusy}
            startIcon={
              revokeBusy ? <CircularProgress size={14} color="inherit" /> : undefined
            }
            sx={{
              minHeight: 44,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 13,
              textTransform: 'none',
              backgroundColor: 'error.main',
              '&:hover': { backgroundColor: 'error.dark' },
            }}
          >
            {revokeBusy ? 'revoking…' : 'revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Row item — one PAT in the list
// ---------------------------------------------------------------------------

function PatRowItem({ row, onRevoke }: { row: PatRow; onRevoke: () => void }) {
  const revoked = row.revoked_at !== null;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={2}
      sx={{ py: 2 }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 14,
            fontWeight: 600,
            color: revoked ? 'text.disabled' : 'text.primary',
          }}
        >
          {row.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            color: 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {row.token_prefix}… · created {formatRelative(row.created_at)} ·{' '}
          {row.last_used_at ? `last used ${formatRelative(row.last_used_at)}` : 'never used'}
        </Typography>
      </Box>
      <Typography
        component="span"
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 11,
          textTransform: 'uppercase',
          color: revoked ? 'text.disabled' : 'success.main',
          letterSpacing: '0.06em',
        }}
      >
        {revoked ? 'revoked' : 'active'}
      </Typography>
      {!revoked && (
        <Button
          variant="text"
          onClick={onRevoke}
          aria-label={`Revoke ${row.name}`}
          sx={{
            minHeight: 44,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 12,
            textTransform: 'none',
            color: 'error.main',
            '&:hover': { color: 'error.dark', backgroundColor: 'transparent' },
          }}
        >
          revoke
        </Button>
      )}
    </Stack>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const ms = Date.now() - then;
  if (ms < 0) return 'just now';
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (ms < minute) return 'just now';
  if (ms < hour) {
    const n = Math.floor(ms / minute);
    return `${n} minute${n === 1 ? '' : 's'} ago`;
  }
  if (ms < day) {
    const n = Math.floor(ms / hour);
    return `${n} hour${n === 1 ? '' : 's'} ago`;
  }
  const n = Math.floor(ms / day);
  return `${n} day${n === 1 ? '' : 's'} ago`;
}
